import { useMemo } from "react";
import Table from "../../../../Components/Table/Table";
import Card from "./Card";
import TableSkeleton from "Components/Common/Skeleton/TableSkeleton";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import EmptyDrawHistoryIcon from "Assets/Img/EmptyDrawHistoryIcon";
import { Draw, formatStatus } from "./DrawHistoryTable";

interface DrawByDrawBillingTableProps {
  draws?: Draw[];
  contractSumToDate?: number;
  loading?: boolean;
}

interface BillingRow {
  id: string;
  drawNumber: number | string;
  period: string;
  amount: string;
  stored: string;
  cumulative: string;
  pctOfContract: string;
  status: string;
  statusVariant: string;
  isCurrent: boolean;
}

const STATUS_VARIANT: Record<string, string> = {
  owner_signature_pending: "pending",
  inspection_pending: "pending",
  approved: "active",
  cancelled: "danger",
  submitted: "pending",
  draft: "pending",
};

const TERMINAL_STATUSES = ["approved", "denied", "cancelled"];

const formatDate = (val?: string): string => {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d.getTime())
    ? val
    : d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};

const formatMoney = (amount: number, symbol = "$"): string =>
  `${symbol}${amount.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const STATUS_ICON: Record<string, string> = {
  active: "✓",
  pending: "⏳",
  danger: "✕",
};

export default function DrawByDrawBillingTable({
  draws = [],
  contractSumToDate,
  loading = false,
}: DrawByDrawBillingTableProps) {
  const rows = useMemo<BillingRow[]>(() => {
    const sorted = [...draws].sort(
      (a, b) => (a.drawNumber ?? 0) - (b.drawNumber ?? 0),
    );
    const symbol = sorted.find((d) => d.requestedAmount)?.requestedAmount
      ?.currency?.symbol;
    let cumulative = 0;
    return sorted.map((d) => {
      const amount = d.requestedAmount?.amount ?? 0;
      cumulative += amount;
      const pct =
        contractSumToDate && contractSumToDate > 0
          ? (cumulative / contractSumToDate) * 100
          : null;
      return {
        id: d.id,
        drawNumber: d.drawNumber ?? "—",
        period: formatDate(d.toDate),
        amount: formatMoney(amount, symbol),
        stored:
          d.stored?.amount != null
            ? formatMoney(d.stored.amount, symbol)
            : "—",
        cumulative: formatMoney(cumulative, symbol),
        pctOfContract: pct != null ? `${pct.toFixed(1)}%` : "—",
        status: formatStatus(d.status),
        statusVariant: STATUS_VARIANT[d.status ?? ""] ?? "",
        isCurrent:
          d.status !== "draft" && !TERMINAL_STATUSES.includes(d.status ?? ""),
      };
    });
  }, [draws, contractSumToDate]);

  const columns = [
    {
      header: "Draw",
      className: "center",
      render: (row: BillingRow) => (
        <div className={`drawNumber ${row.statusVariant}`}>
          {String(row.drawNumber).padStart(2, "0")}
        </div>
      ),
    },
    { header: "Period", className: "left", render: (row: BillingRow) => row.period },
    {
      header: "Amount (Col E)",
      className: "right",
      render: (row: BillingRow) => row.amount,
    },
    {
      header: "Stored (F)",
      className: "right",
      render: (row: BillingRow) => row.stored,
    },
    {
      header: "Cumulative",
      className: "right",
      render: (row: BillingRow) => row.cumulative,
    },
    {
      header: "% of Contract",
      className: "right",
      render: (row: BillingRow) => row.pctOfContract,
    },
    {
      header: "Status",
      className: "right",
      render: (row: BillingRow) => (
        <div className={`drawStatusCell ${row.statusVariant}`.trim()}>
          <span className="drawStatusIcon">
            {STATUS_ICON[row.statusVariant] ?? ""}
          </span>
          <span className="drawStatusLabel">{row.status}</span>
        </div>
      ),
    },
  ];

  return (
    <Card title="Draw-by-Draw History">
      {loading ? (
        <TableSkeleton cols={7} rows={4} />
      ) : (
        <Table
          tableClassName="drawByDrawTable"
          data={rows}
          columns={columns}
          rowClassName={(row) => (row.isCurrent ? "currentDraw" : "")}
          fallbackUI={
            <EmptyState
              noBorder
              height="100px"
              icon={<EmptyDrawHistoryIcon />}
              title="No draws on record."
            />
          }
        />
      )}
    </Card>
  );
}
